import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { faqQuestionsApi, clientReferencesApi } from '../../services/webApi';

const Home = () => {
  const [faqQuestions, setFaqQuestions] = useState([]);
  const [faqLoading, setFaqLoading] = useState(true);
  const [references, setReferences] = useState([]);

  const fetchFaqQuestions = async () => {
    try {
      const response = await faqQuestionsApi.getForHomepage();
      if (response.data.success) {
        setFaqQuestions(response.data.data);
      }
    } catch (err) {
      console.error('FAQ questions fetch error:', err);
    } finally {
      setFaqLoading(false);
    }
  };

  const fetchReferences = async () => {
    try {
      const response = await clientReferencesApi.getAll();
      if (response.data.success) {
        // Shuffle array for random order
        const shuffled = [...response.data.data].sort(() => 0.5 - Math.random());
        setReferences(shuffled);
      }
    } catch (err) {
      console.error('References fetch error:', err);
      // Fallback to static data
      setReferences([
        { company_name: 'MerkürNet', logo: '/web-theme/images/logo/merkurnet.png' },
        { company_name: 'İnternet Kutusu', logo: '/web-theme/images/logo/internet-kutusu.png' },
        { company_name: 'TrendNET', logo: '/web-theme/images/logo/trendnet.png' },
        { company_name: 'Daha Online', logo: '/web-theme/images/logo/daha-online.webp' },
        { company_name: 'MaviNet', logo: '/web-theme/images/logo/mavinet.png' },
        { company_name: 'DijitalNet', logo: '/web-theme/images/logo/dijitalnet.png' },
        { company_name: 'ArasNet', logo: '/web-theme/images/logo/arasnet.png' },
        { company_name: 'GöktürkNet', logo: '/web-theme/images/logo/gokturknet.png' },
        { company_name: 'İnciNet', logo: '/web-theme/images/logo/incinet.svg' }
      ].sort(() => 0.5 - Math.random()));
    }
  };

  useEffect(() => {
    fetchFaqQuestions();
    fetchReferences();

    // Initialize AOS
    if (window.AOS) {
      window.AOS.init();
    }

    // Initialize Preline
    if (window.HSStaticMethods) {
      window.HSStaticMethods.autoInit();
    }
  }, []);

  // Initialize Lucide icons after FAQ questions are loaded
  useEffect(() => {
    if (!faqLoading && window.lucide) {
      const timer = setTimeout(() => {
        window.lucide.createIcons();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [faqLoading]);

  const marqueeReferences = references.length > 0 ? [...references, ...references] : [];

  return (
    <>
      {/* Hero Section Start */}
      <section id="home" className="relative overflow-hidden pt-[72px] backdrop-blur-3xl">
        <div className="overlay"></div>
        <div className="stars" aria-hidden="true"></div>
        <div className="starts2" aria-hidden="true"></div>
        <div className="stars3" aria-hidden="true"></div>
        <div className="absolute h-14 w-14 bg-primary/10 top-2/3 start-80 -z-1 rounded-2xl rounded-tl-none rounded-br-none animate-[spin_10s_linear_infinite]"></div>
        <div className="absolute h-14 w-14 bg-primary/20 top-2/3 end-80 -z-1 rounded-full animate-ping"></div>

        <div className="px-6 pt-20 overflow-hidden" data-aos="fade-up" data-aos-easing="ease" data-aos-duration="800">
          <div className="relative">
            <div className="absolute left-1/2 top-4 h-[1026px] w-[1026px] -translate-x-1/3 stroke-default-300/70 [mask-image:linear-gradient(to_bottom,white_20%,transparent_75%)] sm:top-16 sm:-translate-x-1/2 lg:-top-16 lg:ml-12 xl:-top-14 xl:ml-0">
              <svg viewBox="0 0 1026 1026" fill="none" aria-hidden="true" className="absolute inset-0 h-full w-full animate-spin-slow">
                <path className="stroke-white/10" d="M1025 513c0 282.77-229.23 512-512 512S1 795.77 1 513 230.23 1 513 1s512 229.23 512 512Z" strokeOpacity="0.7"></path>
                <path d="M513 1025C230.23 1025 1 795.77 1 513" stroke="url(#:S2:-gradient-1)" strokeLinecap="round"></path>
                <defs>
                  <linearGradient id=":S2:-gradient-1" x1="1" y1="513" x2="1" y2="1025" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#7c3aed"></stop>
                    <stop offset="1" stopColor="#7c3aed" stopOpacity="0"></stop>
                  </linearGradient>
                </defs>
              </svg>
              <svg viewBox="0 0 1026 1026" fill="none" aria-hidden="true" className="absolute inset-0 h-full w-full animate-spin-reverse-slower">
                <path className="stroke-white/10" d="M913 513c0 220.914-179.086 400-400 400S113 733.914 113 513s179.086-400 400-400 400 179.086 400 400Z" strokeOpacity="0.7"></path>
                <path d="M913 513c0 220.914-179.086 400-400 400" stroke="url(#:S2:-gradient-2)" strokeLinecap="round"></path>
                <defs>
                  <linearGradient id=":S2:-gradient-2" x1="913" y1="513" x2="913" y2="913" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#7c3aed"></stop>
                    <stop offset="1" stopColor="#7c3aed" stopOpacity="0"></stop>
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <div className="container">
              <div className="py-20 lg:py-28 text-center relative">
                <div className="flex justify-center">
                  <div className="max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/5 mb-8">
                      <span className="h-2 w-2 rounded-full bg-primary animate-pulse"></span>
                      <span className="text-xs font-medium text-primary tracking-wide uppercase">ISS Abone Yönetim Sistemi</span>
                    </div>
                    <h2 className="md:text-6xl/tight text-4xl text-default-100 font-bold mb-6">
                      <span className="bg-gradient-to-l from-rose-500 to-primary text-transparent bg-clip-text">ISP Radius ile Tanışın</span><br/>
                      Tüm İşlemlerinizi Tek Noktadan Kontrol Edin
                    </h2>
                    <p className="text-base font-medium text-default-400 mt-5 max-w-2xl mx-auto leading-relaxed">
                      ISP Radius, ön muhasebe, sipariş, stok, depo, abone, cihaz ve daha fazlasını tek bir panele entegre ederek işlemlerinizi tek bir noktadan hızlıca kontrol etmenizi sağlar. Telekomünikasyon sektörü ile %100 uyumlu olan CRM yazılımımızla tanışın ve işlerinizi kolaylaştırın.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
                      <Link to="/iletisim" className="inline-flex items-center justify-center gap-2 bg-primary text-white py-3 px-8 rounded-full hover:bg-primary-hover transition-all duration-300 shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:scale-105">
                        Ücretsiz Demo Talebi
                        <i data-lucide="move-right" className="h-5 w-5"></i>
                      </Link>
                      <Link to="/ozellikler" className="inline-flex items-center justify-center gap-2 border border-slate-200/90 bg-white/70 text-slate-700 py-3 px-8 rounded-full hover:bg-white hover:border-slate-300 transition-all duration-300 shadow-sm">
                        Özelliklerimiz
                        <i data-lucide="chevron-right" className="h-4 w-4"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Hero end */}

      {/* Logo Swiper Section */}
      <section className="py-20 relative">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
        <div className="container" data-aos="fade-up" data-aos-easing="ease" data-aos-duration="800">
          <div className="relative overflow-hidden">
            <style>{`
              @keyframes logo-marquee {
                0% { transform: translateX(0); }
                100% { transform: translateX(-50%); }
              }
              .logo-marquee-track {
                animation: logo-marquee 36s linear infinite;
              }
              .logo-marquee-track:hover {
                animation-play-state: paused;
              }
            `}</style>

            <div className="logo-marquee-track flex w-max items-center gap-5">
              {marqueeReferences.map((reference, index) => (
                <div
                  key={`${reference.id || index}-${reference.company_name}-${index}`}
                  className="flex-shrink-0"
                  style={{ width: '190px', height: '100px' }}
                >
                  <div
                    className="flex items-center justify-center w-full h-full rounded-xl px-6 py-4 border border-white/40 transition-all duration-300 hover:shadow-lg"
                    style={{ backgroundColor: '#ffffff', boxShadow: '0 4px 18px rgba(0, 0, 0, 0.15)' }}
                  >
                    <img 
                      src={reference.logo} 
                      alt={reference.company_name}
                      className="max-h-full max-w-full object-contain transition-opacity duration-300"
                      style={{ 
                        objectFit: 'contain',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        opacity: 1,
                        filter: 'none'
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="pointer-events-none absolute inset-y-0 left-0 w-20 isp-marquee-fade-l" aria-hidden />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-20 isp-marquee-fade-r" aria-hidden />
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section id="generator" className="py-24 relative">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
        <div className="container">
          <div className="flex items-end justify-between mb-14" data-aos="fade-up" data-aos-easing="ease" data-aos-duration="800">
            <div className="max-w-2xl mx-auto text-center">
              <span className="text-xs font-semibold text-primary uppercase tracking-widest">Neden ISP Radius?</span>
              <h2 className="text-3xl font-semibold text-white mt-3 mb-4">Avantajlarımız</h2>
              <p className="text-sm text-default-300 font-medium leading-relaxed">
                Şirketinizin gereksinimlerine uygun, güçlü ve esnek bir CRM çözümü arıyorsanız, size uygun çözüm burada! İşte size sunduğumuz avantajlar
              </p>
            </div>
          </div>

          <div className="grid xl:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6" data-aos="fade-up" data-aos-easing="ease" data-aos-duration="800">
            <div className="group p-8 rounded-2xl bg-default-950/40 border border-white/5 transition-all duration-500 hover:-translate-y-2 hover:bg-primary/10 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 text-center">
              <svg viewBox="0 0 80 80" className="w-16 h-16 mx-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
                <style>{`
                  @keyframes av1-cursor{0%,100%{opacity:1}50%{opacity:0}}
                  @keyframes av1-click{0%{r:3}50%{r:6;opacity:.3}100%{r:3;opacity:1}}
                  .av1-cursor{animation:av1-cursor 1.2s ease-in-out infinite}
                  .av1-click{animation:av1-click 2s ease-in-out infinite}
                `}</style>
                <rect x="4" y="8" width="72" height="52" rx="8" fill="#7c3aed" opacity="0.1" stroke="#7c3aed" strokeWidth="1.5"/>
                <rect x="4" y="8" width="72" height="14" rx="8" fill="#7c3aed" opacity="0.15"/>
                <rect x="4" y="18" width="72" height="4" fill="#7c3aed" opacity="0.15"/>
                <circle cx="14" cy="15" r="2.5" fill="#ef4444"/><circle cx="22" cy="15" r="2.5" fill="#eab308"/><circle cx="30" cy="15" r="2.5" fill="#22c55e"/>
                <rect x="12" y="28" width="28" height="4" rx="2" fill="#a855f7" opacity="0.5"/>
                <rect x="12" y="36" width="56" height="3" rx="1.5" fill="#cbd5e1"/>
                <rect x="12" y="43" width="40" height="3" rx="1.5" fill="#cbd5e1"/>
                <rect x="12" y="50" width="48" height="3" rx="1.5" fill="#cbd5e1"/>
                <rect x="56" y="28" width="12" height="4" rx="2" fill="#10b981" opacity="0.6"/>
                <circle cx="58" cy="48" r="3" fill="#a855f7" className="av1-click" opacity="0.7"/>
                <path d="M56 46 L60 50 L64 42" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.8"/>
                <rect x="16" y="66" width="48" height="6" rx="3" fill="#7c3aed" opacity="0.2"/>
                <rect x="16" y="66" width="32" height="6" rx="3" fill="#7c3aed" opacity="0.5"/>
                <circle cx="48" cy="69" r="2" fill="#a855f7" className="av1-cursor"/>
              </svg>
              <h3 className="text-xl font-medium text-default-200 mt-8">Kullanıcı Dostu Arayüz</h3>
              <p className="text-base font-normal text-default-300 mt-4">
                İşlemlerinizi kolayca yönetmenizi sağlayan ve kullanıcı deneyimini en üst düzeye çıkaran bir arayüz sunuyoruz.
              </p>
            </div>

            <div className="group p-8 rounded-2xl bg-default-950/40 border border-white/5 transition-all duration-500 hover:-translate-y-2 hover:bg-primary/10 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 text-center">
              <svg viewBox="0 0 80 80" className="w-16 h-16 mx-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
                <style>{`
                  @keyframes av2-spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}
                  @keyframes av2-pulse{0%,100%{opacity:.3}50%{opacity:1}}
                  @keyframes av2-flow{0%{stroke-dashoffset:0}100%{stroke-dashoffset:-12}}
                  .av2-gear{animation:av2-spin 6s linear infinite;transform-origin:40px 40px}
                  .av2-pulse{animation:av2-pulse 2s ease-in-out infinite}
                  .av2-flow{stroke-dasharray:6 6;animation:av2-flow 1.5s linear infinite}
                `}</style>
                <circle cx="40" cy="40" r="36" fill="#7c3aed" opacity="0.05" stroke="#7c3aed" strokeWidth="0.8" strokeDasharray="4 4"/>
                <g className="av2-gear">
                  <circle cx="40" cy="40" r="10" fill="#7c3aed" opacity="0.15" stroke="#a855f7" strokeWidth="1.2"/>
                  <circle cx="40" cy="40" r="4" fill="#a855f7" opacity="0.4"/>
                  {[0,45,90,135,180,225,270,315].map((angle, i) => (
                    <rect key={i} x="38" y="26" width="4" height="6" rx="2" fill="#a855f7" opacity="0.6" transform={`rotate(${angle} 40 40)`}/>
                  ))}
                </g>
                <rect x="6" y="12" width="18" height="14" rx="4" fill="#f8fafc" stroke="#3b82f6" strokeWidth="1"/>
                <text x="15" y="22" textAnchor="middle" fill="#60a5fa" fontSize="6" fontFamily="system-ui,sans-serif" fontWeight="700">TT</text>
                <rect x="56" y="12" width="18" height="14" rx="4" fill="#f8fafc" stroke="#10b981" strokeWidth="1"/>
                <text x="65" y="22" textAnchor="middle" fill="#34d399" fontSize="5" fontFamily="system-ui,sans-serif" fontWeight="700">NKT</text>
                <rect x="6" y="56" width="18" height="14" rx="4" fill="#f8fafc" stroke="#f59e0b" strokeWidth="1"/>
                <text x="15" y="66" textAnchor="middle" fill="#fbbf24" fontSize="5" fontFamily="system-ui,sans-serif" fontWeight="700">MK</text>
                <rect x="56" y="56" width="18" height="14" rx="4" fill="#f8fafc" stroke="#ef4444" strokeWidth="1"/>
                <text x="65" y="66" textAnchor="middle" fill="#f87171" fontSize="5" fontFamily="system-ui,sans-serif" fontWeight="700">API</text>
                <line x1="24" y1="19" x2="30" y2="34" stroke="#3b82f6" strokeWidth="1" className="av2-flow" opacity="0.5"/>
                <line x1="56" y1="19" x2="50" y2="34" stroke="#10b981" strokeWidth="1" className="av2-flow" opacity="0.5"/>
                <line x1="24" y1="63" x2="30" y2="48" stroke="#f59e0b" strokeWidth="1" className="av2-flow" opacity="0.5"/>
                <line x1="56" y1="63" x2="50" y2="48" stroke="#ef4444" strokeWidth="1" className="av2-flow" opacity="0.5"/>
                <circle cx="15" cy="19" r="2" className="av2-pulse" fill="#3b82f6"/>
                <circle cx="65" cy="19" r="2" className="av2-pulse" fill="#10b981" style={{animationDelay: '0.5s'}}/>
                <circle cx="15" cy="63" r="2" className="av2-pulse" fill="#f59e0b" style={{animationDelay: '1s'}}/>
                <circle cx="65" cy="63" r="2" className="av2-pulse" fill="#ef4444" style={{animationDelay: '1.5s'}}/>
              </svg>
              <h3 className="text-xl font-medium text-default-200 mt-8">Geniş Entegrasyon Seçenekleri</h3>
              <p className="text-base font-normal text-default-300 mt-4">
                İş süreçlerinizi optimize etmek ve farklı platformlarla sorunsuz bir şekilde çalışmak için çeşitli entegrasyon seçenekleri sunuyoruz.
              </p>
            </div>

            <div className="group p-8 rounded-2xl bg-default-950/40 border border-white/5 transition-all duration-500 hover:-translate-y-2 hover:bg-primary/10 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 text-center">
              <svg viewBox="0 0 80 80" className="w-16 h-16 mx-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
                <style>{`
                  @keyframes av3-grow{0%{transform:scaleY(0)}100%{transform:scaleY(1)}}
                  @keyframes av3-shine{0%,100%{opacity:.5}50%{opacity:1}}
                  .av3-bar{animation:av3-grow .8s ease-out both;transform-origin:bottom}
                  .av3-b1{animation-delay:.2s}.av3-b2{animation-delay:.4s}.av3-b3{animation-delay:.6s}.av3-b4{animation-delay:.8s}
                  .av3-shine{animation:av3-shine 2s ease-in-out infinite}
                `}</style>
                <rect x="4" y="4" width="72" height="72" rx="12" fill="#7c3aed" opacity="0.05"/>
                <rect x="12" y="54" width="10" height="18" rx="3" fill="#3b82f6" opacity="0.6" className="av3-bar av3-b1"/>
                <rect x="26" y="42" width="10" height="30" rx="3" fill="#7c3aed" opacity="0.6" className="av3-bar av3-b2"/>
                <rect x="40" y="34" width="10" height="38" rx="3" fill="#10b981" opacity="0.6" className="av3-bar av3-b3"/>
                <rect x="54" y="24" width="10" height="48" rx="3" fill="#a855f7" opacity="0.8" className="av3-bar av3-b4"/>
                <path d="M14 50 L30 38 L44 30 L58 20" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.7"/>
                <circle cx="58" cy="20" r="3" fill="#34d399" className="av3-shine"/>
                <path d="M55 20 L58 16 L62 20" stroke="#34d399" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <text x="40" y="14" textAnchor="middle" fill="#a855f7" fontSize="7" fontFamily="system-ui,sans-serif" fontWeight="700">₺</text>
                <circle cx="40" cy="12" r="6" fill="#7c3aed" opacity="0.1" stroke="#a855f7" strokeWidth="0.8"/>
              </svg>
              <h3 className="text-xl font-medium text-default-200 mt-8">Ekonomik Fiyatlama Sistemi</h3>
              <p className="text-base font-normal text-default-300 mt-4">
                Sektördeki en rekabetçi fiyatlarla, ISP Radius'un birçok özelliğine sahip olabilirsiniz. Böylece bütçenizi aşmadan ihtiyaçlarınızı karşılayabilirsiniz.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Management Section */}
      <section className="py-24 relative">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
        <div className="container">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div data-aos="fade-right" data-aos-easing="ease" data-aos-duration="1000">
              <span className="py-1 px-3 rounded-md text-sm font-medium uppercase tracking-wider text-primary bg-primary/20">Abone Yönetimi Kolaylığı</span>
              <h2 className="text-3xl/snug font-medium capitalize text-white mt-4">Hizmetleri Tek Noktadan Yönetin</h2>
              <p className="text-base font-medium text-default-200 mt-5">
                Bir abonenin Fiber, VDSL, Statik IP gibi tüm hizmetlerini tek bir ekrandan görüntüleyin ve yönetin. PPPoE bilgileri, statik IP atamaları ve fatura detayları her hizmetin altında anlık olarak görünür. Ayrı ayrı ekranlar arasında gezinmenize gerek kalmadan, tek bir abone kartından tüm işlemleri hızlıca gerçekleştirin.
              </p>
            </div>

            <div data-aos="fade-left" data-aos-easing="ease" data-aos-duration="1000">
              <svg viewBox="0 0 520 460" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
                <defs>
                  <linearGradient id="grad-purple" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#7c3aed"/><stop offset="100%" stopColor="#a855f7"/></linearGradient>
                  <linearGradient id="grad-blue" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#3b82f6"/><stop offset="100%" stopColor="#60a5fa"/></linearGradient>
                </defs>
                <style>{`
                  @keyframes sv1-pulse{0%,100%{opacity:.3}50%{opacity:.8}}
                  @keyframes sv1-draw{0%{stroke-dashoffset:80}100%{stroke-dashoffset:0}}
                  @keyframes sv1-in{0%{opacity:0;transform:translateY(10px)}100%{opacity:1;transform:translateY(0)}}
                  @keyframes sv1-dot{0%,100%{fill:#10b981}50%{fill:#34d399}}
                  .sv1-line{stroke-dasharray:80;animation:sv1-draw 1.5s ease-out forwards}
                  .sv1-ld1{animation-delay:.3s;stroke-dashoffset:80}
                  .sv1-ld2{animation-delay:.7s;stroke-dashoffset:80}
                  .sv1-c{animation:sv1-in .6s ease-out both}
                  .sv1-cd1{animation-delay:.5s}.sv1-cd2{animation-delay:.9s}.sv1-cd3{animation-delay:1.2s}
                  .sv1-dot{animation:sv1-dot 2s ease-in-out infinite}
                  .sv1-dot2{animation-delay:.5s}
                  .sv1-ring{animation:sv1-pulse 3s ease-in-out infinite}
                `}</style>

                {/* Panel */}
                <rect x="20" y="10" width="480" height="440" rx="16" fill="#eef2f7" stroke="#cbd5e1" strokeWidth="1.5"/>
                <rect x="20" y="10" width="480" height="44" rx="16" fill="#f8fafc"/>
                <rect x="20" y="38" width="480" height="16" fill="#f8fafc"/>
                <circle cx="46" cy="32" r="6" fill="#ef4444"/><circle cx="64" cy="32" r="6" fill="#eab308"/><circle cx="82" cy="32" r="6" fill="#22c55e"/>
                <text x="260" y="36" textAnchor="middle" fill="#94a3b8" fontSize="11" fontFamily="system-ui,sans-serif" fontWeight="600">ISP Radius - Abone Paneli</text>

                {/* Subscriber card */}
                <rect x="40" y="66" width="440" height="56" rx="12" fill="#f8fafc" stroke="#7c3aed" strokeWidth="1.5"/>
                <circle cx="76" cy="94" r="16" fill="url(#grad-purple)" opacity="0.2" stroke="#7c3aed" strokeWidth="1.5"/>
                <circle cx="76" cy="89" r="5.5" fill="#c4b5fd"/>
                <path d="M67 101 C67 97 71 94 76 94 C81 94 85 97 85 101" fill="#c4b5fd"/>
                <text x="102" y="88" fill="#334155" fontSize="12" fontFamily="system-ui,sans-serif" fontWeight="600">Sezer ÇOMAK</text>
                <text x="102" y="104" fill="#94a3b8" fontSize="9" fontFamily="system-ui,sans-serif">Abone No: 3500000000</text>
                <rect x="340" y="80" width="64" height="20" rx="6" fill="#7c3aed" opacity="0.15"/>
                <text x="372" y="94" textAnchor="middle" fill="#a855f7" fontSize="8" fontFamily="system-ui,sans-serif" fontWeight="600">2 Hizmet</text>
                <rect x="410" y="80" width="58" height="20" rx="6" fill="#10b981" opacity="0.15"/>
                <text x="439" y="94" textAnchor="middle" fill="#34d399" fontSize="8" fontFamily="system-ui,sans-serif" fontWeight="600">Aktif</text>

                {/* Connection lines */}
                <path d="M180 122 L180 142 L152 142 L152 158" stroke="#7c3aed" strokeWidth="1.5" strokeDasharray="4 3" className="sv1-line sv1-ld1" opacity="0.5"/>
                <path d="M340 122 L340 142 L368 142 L368 158" stroke="#3b82f6" strokeWidth="1.5" strokeDasharray="4 3" className="sv1-line sv1-ld2" opacity="0.5"/>

                {/* Service 1: Fiber - with Statik IP */}
                <g className="sv1-c sv1-cd1">
                  <rect x="40" y="158" width="220" height="190" rx="12" fill="#f8fafc" stroke="#7c3aed" strokeWidth="1" opacity="0.7"/>
                  {/* Header */}
                  <rect x="40" y="158" width="220" height="38" rx="12" fill="#7c3aed" opacity="0.08"/>
                  <rect x="52" y="166" width="24" height="24" rx="6" fill="url(#grad-purple)" opacity="0.25"/>
                  <path d="M59 182 C59 182 59 182 64 177 C69 182 69 182 69 182" stroke="#a855f7" strokeWidth="1.5" strokeLinecap="round" fill="none" className="sv1-ring"/>
                  <circle cx="64" cy="181" r="1.5" fill="#a855f7"/>
                  <text x="82" y="176" fill="#334155" fontSize="11" fontFamily="system-ui,sans-serif" fontWeight="600">Fiber Internet</text>
                  <text x="82" y="188" fill="#94a3b8" fontSize="7.5" fontFamily="system-ui,sans-serif">100 Mbps</text>
                  <circle cx="232" cy="177" r="4" className="sv1-dot" fill="#10b981"/>
                  <text x="222" y="180" fill="#94a3b8" fontSize="7" fontFamily="system-ui,sans-serif" textAnchor="end">Aktif</text>

                  {/* PPPoE */}
                  <text x="52" y="212" fill="#64748b" fontSize="7" fontFamily="system-ui,sans-serif" fontWeight="500">PPPoE</text>
                  <rect x="52" y="216" width="196" height="18" rx="4" fill="#eef2f7"/>
                  <text x="60" y="229" fill="#a855f7" fontSize="8.5" fontFamily="system-ui,sans-serif" fontWeight="500">6400000001@ispradius</text>

                  {/* Statik IP tag */}
                  <rect x="52" y="242" width="196" height="28" rx="6" fill="#10b981" fillOpacity="0.06" stroke="#10b981" strokeWidth="0.5" strokeOpacity="0.3"/>
                  <rect x="58" y="248" width="10" height="10" rx="3" fill="#10b981" opacity="0.2"/>
                  <text x="63" y="256" textAnchor="middle" fill="#34d399" fontSize="7" fontFamily="system-ui,sans-serif" fontWeight="700">IP</text>
                  <text x="76" y="256" fill="#334155" fontSize="8" fontFamily="system-ui,sans-serif" fontWeight="500">Statik IP</text>
                  <text x="236" y="256" fill="#34d399" fontSize="8" fontFamily="system-ui,sans-serif" textAnchor="end">85.105.12.34</text>
                  <circle cx="242" cy="253" r="3" className="sv1-dot" fill="#10b981"/>

                  {/* Price */}
                  <rect x="52" y="278" width="196" height="1" fill="#cbd5e1" opacity="0.5"/>
                  <text x="52" y="296" fill="#64748b" fontSize="7.5" fontFamily="system-ui,sans-serif">Aylık Tutar</text>
                  <text x="236" y="296" fill="#334155" fontSize="10" fontFamily="system-ui,sans-serif" fontWeight="600" textAnchor="end">₺449</text>
                  <rect x="52" y="304" width="196" height="5" rx="2.5" fill="#eef2f7"/>
                  <rect x="52" y="304" width="160" height="5" rx="2.5" fill="#7c3aed" opacity="0.5"/>
                  <text x="52" y="320" fill="#64748b" fontSize="6.5" fontFamily="system-ui,sans-serif">Fiber ₺349 + Statik IP ₺100</text>
                  <rect x="52" y="328" width="196" height="14" rx="4" fill="#7c3aed" opacity="0.1"/>
                  <text x="150" y="339" textAnchor="middle" fill="#a855f7" fontSize="7.5" fontFamily="system-ui,sans-serif" fontWeight="500">Hizmet Detayı</text>
                </g>

                {/* Service 2: VDSL - no Statik IP */}
                <g className="sv1-c sv1-cd2">
                  <rect x="270" y="158" width="210" height="190" rx="12" fill="#f8fafc" stroke="#3b82f6" strokeWidth="1" opacity="0.7"/>
                  {/* Header */}
                  <rect x="270" y="158" width="210" height="38" rx="12" fill="#3b82f6" opacity="0.08"/>
                  <rect x="282" y="166" width="24" height="24" rx="6" fill="url(#grad-blue)" opacity="0.25"/>
                  <circle cx="294" cy="178" r="8" stroke="#60a5fa" strokeWidth="1.3" fill="none"/>
                  <ellipse cx="294" cy="178" rx="4.5" ry="8" stroke="#60a5fa" strokeWidth="0.8" fill="none" opacity="0.5"/>
                  <line x1="286" y1="178" x2="302" y2="178" stroke="#60a5fa" strokeWidth="0.8" opacity="0.4"/>
                  <text x="312" y="176" fill="#334155" fontSize="11" fontFamily="system-ui,sans-serif" fontWeight="600">VDSL</text>
                  <text x="312" y="188" fill="#94a3b8" fontSize="7.5" fontFamily="system-ui,sans-serif">50 Mbps</text>
                  <circle cx="454" cy="177" r="4" className="sv1-dot sv1-dot2" fill="#10b981"/>
                  <text x="444" y="180" fill="#94a3b8" fontSize="7" fontFamily="system-ui,sans-serif" textAnchor="end">Aktif</text>

                  {/* PPPoE */}
                  <text x="282" y="212" fill="#64748b" fontSize="7" fontFamily="system-ui,sans-serif" fontWeight="500">PPPoE</text>
                  <rect x="282" y="216" width="186" height="18" rx="4" fill="#eef2f7"/>
                  <text x="290" y="229" fill="#60a5fa" fontSize="8.5" fontFamily="system-ui,sans-serif" fontWeight="500">6400000002@ispradius</text>

                  {/* No static IP - placeholder */}
                  <rect x="282" y="242" width="186" height="28" rx="6" fill="#cbd5e1" opacity="0.15" stroke="#cbd5e1" strokeWidth="0.5" strokeDasharray="4 2"/>
                  <text x="375" y="260" textAnchor="middle" fill="#64748b" fontSize="8" fontFamily="system-ui,sans-serif">Statik IP Yok</text>

                  {/* Price */}
                  <rect x="282" y="278" width="186" height="1" fill="#cbd5e1" opacity="0.5"/>
                  <text x="282" y="296" fill="#64748b" fontSize="7.5" fontFamily="system-ui,sans-serif">Aylık Tutar</text>
                  <text x="458" y="296" fill="#334155" fontSize="10" fontFamily="system-ui,sans-serif" fontWeight="600" textAnchor="end">₺199</text>
                  <rect x="282" y="304" width="186" height="5" rx="2.5" fill="#eef2f7"/>
                  <rect x="282" y="304" width="120" height="5" rx="2.5" fill="#3b82f6" opacity="0.5"/>
                  <text x="282" y="320" fill="#64748b" fontSize="6.5" fontFamily="system-ui,sans-serif">VDSL ₺199</text>
                  <rect x="282" y="328" width="186" height="14" rx="4" fill="#3b82f6" opacity="0.1"/>
                  <text x="375" y="339" textAnchor="middle" fill="#60a5fa" fontSize="7.5" fontFamily="system-ui,sans-serif" fontWeight="500">Hizmet Detayı</text>
                </g>

                {/* Bottom summary bar */}
                <g className="sv1-c sv1-cd3">
                  <rect x="40" y="362" width="440" height="76" rx="12" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1"/>
                  <text x="56" y="384" fill="#94a3b8" fontSize="8" fontFamily="system-ui,sans-serif">Aylık Toplam</text>
                  <text x="56" y="408" fill="#7c3aed" fontSize="20" fontFamily="system-ui,sans-serif" fontWeight="700">₺648</text>
                  <rect x="140" y="378" width="1" height="44" fill="#cbd5e1"/>
                  <text x="156" y="390" fill="#94a3b8" fontSize="7.5" fontFamily="system-ui,sans-serif">Fiber + Statik IP</text>
                  <text x="260" y="390" fill="#334155" fontSize="8" fontFamily="system-ui,sans-serif" textAnchor="end">₺449</text>
                  <text x="156" y="406" fill="#94a3b8" fontSize="7.5" fontFamily="system-ui,sans-serif">VDSL</text>
                  <text x="260" y="406" fill="#334155" fontSize="8" fontFamily="system-ui,sans-serif" textAnchor="end">₺199</text>
                  <text x="156" y="422" fill="#94a3b8" fontSize="7.5" fontFamily="system-ui,sans-serif">Toplam Hizmet</text>
                  <text x="260" y="422" fill="#334155" fontSize="8" fontFamily="system-ui,sans-serif" textAnchor="end">2 Aktif</text>
                  <rect x="340" y="382" width="128" height="32" rx="8" fill="#7c3aed" opacity="0.15"/>
                  <text x="404" y="402" textAnchor="middle" fill="#a855f7" fontSize="10" fontFamily="system-ui,sans-serif" fontWeight="600">Fatura Oluştur</text>
                  <rect x="340" y="420" width="128" height="14" rx="4" fill="#cbd5e1" opacity="0.3"/>
                  <text x="404" y="430" textAnchor="middle" fill="#94a3b8" fontSize="7.5" fontFamily="system-ui,sans-serif">+ Hizmet Ekle</text>
                </g>
              </svg>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-center mt-28 pt-10">
            <div className="2xl:-ms-16" data-aos="fade-right" data-aos-easing="ease" data-aos-duration="1000">
              <svg viewBox="0 0 520 420" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
                <style>{`
                  @keyframes sv2-in{0%{opacity:0;transform:translateY(8px)}100%{opacity:1;transform:translateY(0)}}
                  @keyframes sv2-blink{0%,100%{opacity:1}50%{opacity:.35}}
                  @keyframes sv2-scan{0%{transform:translateX(-100%)}100%{transform:translateX(200%)}}
                  @keyframes sv2-check{0%{stroke-dashoffset:16}100%{stroke-dashoffset:0}}
                  .sv2-f{animation:sv2-in .5s ease-out both}
                  .sv2-d1{animation-delay:.2s}.sv2-d2{animation-delay:.4s}.sv2-d3{animation-delay:.6s}
                  .sv2-d4{animation-delay:.8s}.sv2-d5{animation-delay:1s}.sv2-d6{animation-delay:1.2s}
                  .sv2-blink{animation:sv2-blink 2s ease-in-out infinite}
                  .sv2-scan-bar{animation:sv2-scan 2.5s ease-in-out infinite}
                  .sv2-chk{stroke-dasharray:16;stroke-dashoffset:16;animation:sv2-check .4s ease-out both;animation-delay:1.6s}
                `}</style>

                {/* Panel */}
                <rect x="20" y="10" width="480" height="400" rx="16" fill="#eef2f7" stroke="#cbd5e1" strokeWidth="1.5"/>
                <rect x="20" y="10" width="480" height="44" rx="16" fill="#f8fafc"/>
                <rect x="20" y="38" width="480" height="16" fill="#f8fafc"/>
                <circle cx="46" cy="32" r="6" fill="#ef4444"/><circle cx="64" cy="32" r="6" fill="#eab308"/><circle cx="82" cy="32" r="6" fill="#22c55e"/>
                <text x="260" y="36" textAnchor="middle" fill="#94a3b8" fontSize="11" fontFamily="system-ui,sans-serif" fontWeight="600">ISP Radius - TT Fatura Analizi</text>

                {/* Scanning progress bar */}
                <rect x="40" y="66" width="455" height="3" rx="1.5" fill="#eef2f7"/>
                <rect x="40" y="66" width="455" height="3" rx="1.5" fill="#7c3aed" opacity="0.15"/>
                <g clipPath="url(#sv2-clip)">
                  <rect x="40" y="66" width="100" height="3" rx="1.5" fill="#7c3aed" opacity="0.6" className="sv2-scan-bar"/>
                </g>
                <defs><clipPath id="sv2-clip"><rect x="40" y="66" width="455" height="3"/></clipPath></defs>

                {/* Module grid - 2x3 */}
                {/* Row 1 */}
                <g className="sv2-f sv2-d1">
                  <rect x="40" y="80" width="220" height="72" rx="10" fill="#f8fafc" stroke="#ef4444" strokeWidth="0.8" opacity="0.5"/>
                  <rect x="52" y="92" width="28" height="28" rx="7" fill="#ef4444" opacity="0.1"/>
                  <path d="M60 102 L66 102 M66 102 L66 108 M66 102 L72 102 M66 102 L66 96" stroke="#f87171" strokeWidth="1.5" strokeLinecap="round"/>
                  <text x="86" y="102" fill="#334155" fontSize="9.5" fontFamily="system-ui,sans-serif" fontWeight="600">Devre Kontrol</text>
                  <text x="86" y="114" fill="#94a3b8" fontSize="7" fontFamily="system-ui,sans-serif">TT'de olup CRM'de olmayan devreler</text>
                  <rect x="52" y="128" width="100" height="14" rx="4" fill="#ef4444" opacity="0.1"/>
                  <text x="58" y="138" fill="#f87171" fontSize="8" fontFamily="system-ui,sans-serif" fontWeight="600">12 devre bulundu</text>
                  <rect x="160" y="128" width="44" height="14" rx="4" fill="#ef4444" opacity="0.15" className="sv2-blink"/>
                  <text x="182" y="138" textAnchor="middle" fill="#f87171" fontSize="7" fontFamily="system-ui,sans-serif" fontWeight="600">UYARI</text>
                </g>

                <g className="sv2-f sv2-d2">
                  <rect x="270" y="80" width="220" height="72" rx="10" fill="#f8fafc" stroke="#f59e0b" strokeWidth="0.8" opacity="0.5"/>
                  <rect x="282" y="92" width="28" height="28" rx="7" fill="#f59e0b" opacity="0.1"/>
                  <path d="M290 108 L296 96 L302 108 Z" stroke="#fbbf24" strokeWidth="1.2" fill="none" strokeLinejoin="round"/>
                  <line x1="296" y1="101" x2="296" y2="104" stroke="#fbbf24" strokeWidth="1.3" strokeLinecap="round"/>
                  <circle cx="296" cy="106" r="0.8" fill="#fbbf24"/>
                  <text x="316" y="102" fill="#334155" fontSize="9.5" fontFamily="system-ui,sans-serif" fontWeight="600">İptal Hizmetler</text>
                  <text x="316" y="114" fill="#94a3b8" fontSize="7" fontFamily="system-ui,sans-serif">TT'de iptal olmamış, CRM'de iptal</text>
                  <rect x="282" y="128" width="100" height="14" rx="4" fill="#f59e0b" opacity="0.1"/>
                  <text x="288" y="138" fill="#fbbf24" fontSize="8" fontFamily="system-ui,sans-serif" fontWeight="600">8 kayıt bulundu</text>
                  <rect x="390" y="128" width="44" height="14" rx="4" fill="#f59e0b" opacity="0.15" className="sv2-blink"/>
                  <text x="412" y="138" textAnchor="middle" fill="#fbbf24" fontSize="7" fontFamily="system-ui,sans-serif" fontWeight="600">UYARI</text>
                </g>

                {/* Row 2 */}
                <g className="sv2-f sv2-d3">
                  <rect x="40" y="162" width="220" height="72" rx="10" fill="#f8fafc" stroke="#3b82f6" strokeWidth="0.8" opacity="0.5"/>
                  <rect x="52" y="174" width="28" height="28" rx="7" fill="#3b82f6" opacity="0.1"/>
                  <path d="M62 184 L66 184 L66 188 M70 192 L66 192 L66 188" stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <text x="86" y="184" fill="#334155" fontSize="9.5" fontFamily="system-ui,sans-serif" fontWeight="600">TT İptal Hizmetler</text>
                  <text x="86" y="196" fill="#94a3b8" fontSize="7" fontFamily="system-ui,sans-serif">CRM'de TT İptal durumunda olanlar</text>
                  <rect x="52" y="210" width="100" height="14" rx="4" fill="#10b981" opacity="0.1"/>
                  <path d="M58 217 L61 220 L67 214" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="sv2-chk"/>
                  <text x="72" y="220" fill="#34d399" fontSize="8" fontFamily="system-ui,sans-serif" fontWeight="600">Tümü eşleşti</text>
                </g>

                <g className="sv2-f sv2-d4">
                  <rect x="270" y="162" width="220" height="72" rx="10" fill="#f8fafc" stroke="#ef4444" strokeWidth="0.8" opacity="0.5"/>
                  <rect x="282" y="174" width="28" height="28" rx="7" fill="#ef4444" opacity="0.1"/>
                  <rect x="289" y="180" width="14" height="10" rx="2" stroke="#f87171" strokeWidth="1.2" fill="none"/>
                  <path d="M289 183 L296 187 L303 183" stroke="#f87171" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <text x="316" y="184" fill="#334155" fontSize="9.5" fontFamily="system-ui,sans-serif" fontWeight="600">HGW Ücreti</text>
                  <text x="316" y="196" fill="#94a3b8" fontSize="7" fontFamily="system-ui,sans-serif">CRM'de HGW ücreti yansımayan kayıtlar</text>
                  <rect x="282" y="210" width="100" height="14" rx="4" fill="#ef4444" opacity="0.1"/>
                  <text x="288" y="220" fill="#f87171" fontSize="8" fontFamily="system-ui,sans-serif" fontWeight="600">5 kayıt bulundu</text>
                  <rect x="390" y="210" width="44" height="14" rx="4" fill="#ef4444" opacity="0.15"/>
                  <text x="412" y="220" textAnchor="middle" fill="#f87171" fontSize="7" fontFamily="system-ui,sans-serif" fontWeight="600">₺1.250</text>
                </g>

                {/* Row 3 */}
                <g className="sv2-f sv2-d5">
                  <rect x="40" y="244" width="220" height="72" rx="10" fill="#f8fafc" stroke="#f59e0b" strokeWidth="0.8" opacity="0.5"/>
                  <rect x="52" y="256" width="28" height="28" rx="7" fill="#f59e0b" opacity="0.1"/>
                  <path d="M62 274 L66 266 L70 274" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <line x1="66" y1="266" x2="66" y2="276" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round"/>
                  <text x="86" y="266" fill="#334155" fontSize="9.5" fontFamily="system-ui,sans-serif" fontWeight="600">Nakil Ücreti Kontrol</text>
                  <text x="86" y="278" fill="#94a3b8" fontSize="7" fontFamily="system-ui,sans-serif">TT'de olup CRM'de yansımayan nakiller</text>
                  <rect x="52" y="292" width="100" height="14" rx="4" fill="#f59e0b" opacity="0.1"/>
                  <text x="58" y="302" fill="#fbbf24" fontSize="8" fontFamily="system-ui,sans-serif" fontWeight="600">3 kayıt bulundu</text>
                  <rect x="160" y="292" width="44" height="14" rx="4" fill="#f59e0b" opacity="0.15"/>
                  <text x="182" y="302" textAnchor="middle" fill="#fbbf24" fontSize="7" fontFamily="system-ui,sans-serif" fontWeight="600">₺2.700</text>
                </g>

                <g className="sv2-f sv2-d6">
                  <rect x="270" y="244" width="220" height="72" rx="10" fill="#f8fafc" stroke="#f59e0b" strokeWidth="0.8" opacity="0.5"/>
                  <rect x="282" y="256" width="28" height="28" rx="7" fill="#f59e0b" opacity="0.1"/>
                  <path d="M292 276 L296 268 L300 276" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  <circle cx="296" cy="272" r="5" stroke="#fbbf24" strokeWidth="1.2" fill="none"/>
                  <text x="316" y="266" fill="#334155" fontSize="9.5" fontFamily="system-ui,sans-serif" fontWeight="600">Bağlantı Ücreti</text>
                  <text x="316" y="278" fill="#94a3b8" fontSize="7" fontFamily="system-ui,sans-serif">TT'de olup CRM'de yansımayan bağlantılar</text>
                  <rect x="282" y="292" width="100" height="14" rx="4" fill="#f59e0b" opacity="0.1"/>
                  <text x="288" y="302" fill="#fbbf24" fontSize="8" fontFamily="system-ui,sans-serif" fontWeight="600">4 kayıt bulundu</text>
                  <rect x="390" y="292" width="44" height="14" rx="4" fill="#f59e0b" opacity="0.15"/>
                  <text x="412" y="302" textAnchor="middle" fill="#fbbf24" fontSize="7" fontFamily="system-ui,sans-serif" fontWeight="600">₺4.500</text>
                </g>

                {/* Bottom action buttons */}
                <g className="sv2-f sv2-d6">
                  <rect x="40" y="332" width="220" height="28" rx="8" fill="#7c3aed" opacity="0.15"/>
                  <text x="150" y="350" textAnchor="middle" fill="#a855f7" fontSize="9" fontFamily="system-ui,sans-serif" fontWeight="600">Detaylı Rapor İndir</text>
                  <rect x="270" y="332" width="220" height="28" rx="8" fill="#ef4444" opacity="0.15"/>
                  <text x="380" y="350" textAnchor="middle" fill="#f87171" fontSize="9" fontFamily="system-ui,sans-serif" fontWeight="600">Uyarıları Gönder</text>
                </g>
              </svg>
            </div>

            <div data-aos="fade-left" data-aos-easing="ease" data-aos-duration="1000">
              <span className="py-1 px-3 rounded-md text-sm font-medium uppercase tracking-wider text-primary bg-primary/20">Türk Telekom Fatura Kontrolü</span>
              <h2 className="text-3xl/snug font-medium capitalize text-white mt-4">Faturalarınızı Kolayca Analiz Edin</h2>
              <p className="text-base font-medium text-default-200 mt-5">
                Türk Telekom'un size kestiği faturaları CRM verilerinizle otomatik olarak karşılaştırın. Sizde olmayan devreler, iptal edilmemiş hizmetler, yansımayan HGW, nakil ve bağlantı ücretlerini anında tespit edin. Fazla ödeme yaptığınız kalemleri tek tıkla raporlayın ve maliyetlerinizi kontrol altına alın.
              </p>
            </div>
          </div>

          <div className="h-16 md:h-24"></div>

          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div data-aos="fade-right" data-aos-easing="ease" data-aos-duration="1000">
              <span className="py-1 px-3 rounded-md text-sm font-medium uppercase tracking-wider text-primary bg-primary/20">Esnek Kurulum</span>
              <h2 className="text-3xl/snug font-medium capitalize text-white mt-4">Sunucunuz Nerede Olsun?</h2>
              <p className="text-lg font-semibold mt-3 bg-gradient-to-l from-rose-500 to-primary text-transparent bg-clip-text">
                İster Sizin Sunucunuz, İster Bizim Bulutumuz.<br />
                Kontrol Tamamen Sizde!
              </p>
              <p className="text-base font-medium text-default-200 mt-5">
                Sınırları kaldırın, altyapınızı siz seçin! ISP Radius olarak size sektörde eşine az rastlanır bir özgürlük sunuyoruz. İş modeliniz hangisine uygunsa, kurulumu oraya yapıyoruz:
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-2 text-default-300 text-sm">
                  <span className="text-primary mt-0.5">✅</span>
                  <span><strong className="text-default-100">Kendi Sunucunuz:</strong> Verilerim benim kontrolümde kalsın, yerel ağımda çalışsın diyorsanız; kurulumu sizin donanımınıza yapıyoruz.</span>
                </li>
                <li className="flex items-start gap-2 text-default-300 text-sm">
                  <span className="text-primary mt-0.5">✅</span>
                  <span><strong className="text-default-100">Bizim Bulutumuz:</strong> Altyapı yönetimiyle, bakımla uğraşmak istemiyorum diyorsanız; yüksek performanslı bulut altyapımızı kullanımınıza açıyoruz.</span>
                </li>
              </ul>
              <p className="text-sm text-default-300 mt-4">
                Rakiplerimizden farklı olarak sizi tek bir modele mahkum etmiyoruz. Donanım bağımlılığını ortadan kaldıran bu esneklikle, her iki senaryoda da aynı güçlü ve kesintisiz ISP Radius deneyimini yaşarsınız.
              </p>
              <p className="text-base font-semibold text-primary mt-4">
                Siz sadece işinizi büyütmeye odaklanın.
              </p>
            </div>

            <div data-aos="fade-left" data-aos-easing="ease" data-aos-duration="1000">
              <svg viewBox="0 0 520 400" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
                <defs>
                  <linearGradient id="grad3-purple" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#7c3aed"/><stop offset="100%" stopColor="#a855f7"/></linearGradient>
                  <linearGradient id="grad3-blue" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#3b82f6"/><stop offset="100%" stopColor="#60a5fa"/></linearGradient>
                </defs>
                <style>{`
                  @keyframes sv3-in{0%{opacity:0;transform:translateY(10px)}100%{opacity:1;transform:translateY(0)}}
                  @keyframes sv3-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
                  @keyframes sv3-pulse{0%,100%{opacity:.4}50%{opacity:1}}
                  @keyframes sv3-dash{0%{stroke-dashoffset:0}100%{stroke-dashoffset:-16}}
                  .sv3-f{animation:sv3-in .6s ease-out both}
                  .sv3-d1{animation-delay:.3s}.sv3-d2{animation-delay:.6s}.sv3-d3{animation-delay:.9s}.sv3-d4{animation-delay:1.2s}
                  .sv3-float{animation:sv3-float 3s ease-in-out infinite}
                  .sv3-float2{animation:sv3-float 3s ease-in-out infinite;animation-delay:1.5s}
                  .sv3-pulse{animation:sv3-pulse 2s ease-in-out infinite}
                  .sv3-flow{stroke-dasharray:8 8;animation:sv3-dash 1.5s linear infinite}
                `}</style>

                {/* Background */}
                <rect x="20" y="10" width="480" height="380" rx="16" fill="#eef2f7" stroke="#cbd5e1" strokeWidth="1.5"/>

                {/* ISP Radius center logo */}
                <g className="sv3-f sv3-d1">
                  <rect x="185" y="28" width="150" height="44" rx="12" fill="#f8fafc" stroke="#7c3aed" strokeWidth="1.5"/>
                  <circle cx="210" cy="50" r="12" fill="url(#grad3-purple)" opacity="0.2" stroke="#7c3aed" strokeWidth="1.2"/>
                  <path d="M205 50 L210 44 L215 50 M210 44 L210 56" stroke="#a855f7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <text x="228" y="53" fill="#334155" fontSize="12" fontFamily="system-ui,sans-serif" fontWeight="700">ISP Radius</text>
                </g>

                {/* OR divider text */}
                <text x="260" y="96" textAnchor="middle" fill="#64748b" fontSize="9" fontFamily="system-ui,sans-serif" fontWeight="500">Nereye kurulsun?</text>

                {/* Animated flow lines from center to options */}
                <path d="M185 96 L130 96 L130 118" stroke="#7c3aed" strokeWidth="1.5" className="sv3-flow" opacity="0.4"/>
                <path d="M335 96 L390 96 L390 118" stroke="#3b82f6" strokeWidth="1.5" className="sv3-flow" opacity="0.4"/>

                {/* Left: Your Server */}
                <g className="sv3-f sv3-d2 sv3-float">
                  <rect x="36" y="118" width="208" height="220" rx="14" fill="#f8fafc" stroke="#7c3aed" strokeWidth="1.2"/>
                  
                  {/* Header */}
                  <rect x="36" y="118" width="208" height="40" rx="14" fill="#7c3aed" opacity="0.08"/>
                  <rect x="36" y="144" width="208" height="14" fill="#7c3aed" opacity="0.08"/>
                  <text x="140" y="143" textAnchor="middle" fill="#a855f7" fontSize="12" fontFamily="system-ui,sans-serif" fontWeight="700">Kendi Sunucunuz</text>

                  {/* Server rack icon */}
                  <rect x="108" y="170" width="44" height="52" rx="6" fill="#7c3aed" opacity="0.08" stroke="#7c3aed" strokeWidth="0.8"/>
                  <rect x="116" y="178" width="28" height="8" rx="2" fill="#eef2f7" stroke="#7c3aed" strokeWidth="0.6"/>
                  <circle cx="120" cy="182" r="1.5" fill="#a855f7" className="sv3-pulse"/>
                  <rect x="126" y="181" width="12" height="2" rx="1" fill="#7c3aed" opacity="0.3"/>
                  <rect x="116" y="190" width="28" height="8" rx="2" fill="#eef2f7" stroke="#7c3aed" strokeWidth="0.6"/>
                  <circle cx="120" cy="194" r="1.5" fill="#a855f7" className="sv3-pulse"/>
                  <rect x="126" y="193" width="12" height="2" rx="1" fill="#7c3aed" opacity="0.3"/>
                  <rect x="116" y="202" width="28" height="8" rx="2" fill="#eef2f7" stroke="#7c3aed" strokeWidth="0.6"/>
                  <circle cx="120" cy="206" r="1.5" fill="#10b981" className="sv3-pulse"/>
                  <rect x="126" y="205" width="12" height="2" rx="1" fill="#7c3aed" opacity="0.3"/>

                  {/* Features list */}
                  <circle cx="60" cy="240" r="3" fill="#10b981"/>
                  <text x="68" y="243" fill="#334155" fontSize="8" fontFamily="system-ui,sans-serif">Veriler tamamen sizde</text>
                  <circle cx="60" cy="258" r="3" fill="#10b981"/>
                  <text x="68" y="261" fill="#334155" fontSize="8" fontFamily="system-ui,sans-serif">Tam kontrol ve erişim</text>
                  <circle cx="60" cy="276" r="3" fill="#10b981"/>
                  <text x="68" y="279" fill="#334155" fontSize="8" fontFamily="system-ui,sans-serif">Özel ağınızda çalışır</text>
                  <circle cx="60" cy="294" r="3" fill="#10b981"/>
                  <text x="68" y="297" fill="#334155" fontSize="8" fontFamily="system-ui,sans-serif">Bağımsız altyapı</text>

                  {/* Badge */}
                  <rect x="66" y="310" width="128" height="20" rx="6" fill="#7c3aed" opacity="0.12"/>
                  <text x="130" y="324" textAnchor="middle" fill="#a855f7" fontSize="8.5" fontFamily="system-ui,sans-serif" fontWeight="600">On-Premise Kurulum</text>
                </g>

                {/* Right: ISP Radius Cloud */}
                <g className="sv3-f sv3-d3 sv3-float2">
                  <rect x="276" y="118" width="208" height="220" rx="14" fill="#f8fafc" stroke="#3b82f6" strokeWidth="1.2"/>
                  
                  {/* Header */}
                  <rect x="276" y="118" width="208" height="40" rx="14" fill="#3b82f6" opacity="0.08"/>
                  <rect x="276" y="144" width="208" height="14" fill="#3b82f6" opacity="0.08"/>
                  <text x="380" y="143" textAnchor="middle" fill="#60a5fa" fontSize="12" fontFamily="system-ui,sans-serif" fontWeight="700">Bizim Sunucumuz</text>

                  {/* Cloud icon */}
                  <path d="M362 196 C362 186 370 178 380 178 C387 178 393 182 396 188 C398 187 400 186 403 186 C410 186 416 192 416 199 C416 206 410 212 403 212 L362 212 C355 212 350 207 350 200 C350 195 353 191 358 190 C359 189 360 188 362 188" fill="#3b82f6" opacity="0.08" stroke="#60a5fa" strokeWidth="1.2"/>
                  <circle cx="380" cy="198" r="3" fill="#60a5fa" className="sv3-pulse" opacity="0.6"/>
                  <path d="M376 198 L380 194 L384 198 M380 194 L380 204" stroke="#60a5fa" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>

                  {/* Features list */}
                  <circle cx="300" cy="240" r="3" fill="#10b981"/>
                  <text x="308" y="243" fill="#334155" fontSize="8" fontFamily="system-ui,sans-serif">Altyapı yönetimi bizde</text>
                  <circle cx="300" cy="258" r="3" fill="#10b981"/>
                  <text x="308" y="261" fill="#334155" fontSize="8" fontFamily="system-ui,sans-serif">Otomatik yedekleme</text>
                  <circle cx="300" cy="276" r="3" fill="#10b981"/>
                  <text x="308" y="279" fill="#334155" fontSize="8" fontFamily="system-ui,sans-serif">%99.9 uptime garantisi</text>
                  <circle cx="300" cy="294" r="3" fill="#10b981"/>
                  <text x="308" y="297" fill="#334155" fontSize="8" fontFamily="system-ui,sans-serif">Kurulum gerektirmez</text>

                  {/* Badge */}
                  <rect x="306" y="310" width="128" height="20" rx="6" fill="#3b82f6" opacity="0.12"/>
                  <text x="370" y="324" textAnchor="middle" fill="#60a5fa" fontSize="8.5" fontFamily="system-ui,sans-serif" fontWeight="600">Cloud (SaaS) Kurulum</text>
                </g>

                {/* Bottom bar */}
                <g className="sv3-f sv3-d4">
                  <rect x="36" y="350" width="448" height="32" rx="8" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1"/>
                  <rect x="36" y="350" width="448" height="32" rx="8" fill="#10b981" opacity="0.04"/>
                  <text x="260" y="371" textAnchor="middle" fill="#34d399" fontSize="9.5" fontFamily="system-ui,sans-serif" fontWeight="600">Hangi modeli seçerseniz seçin, aynı güçlü ve kesintisiz ISP Radius deneyimi</text>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 relative">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
        <div className="container">
          <div className="flex items-end justify-between mb-16" data-aos="fade-up" data-aos-easing="ease" data-aos-duration="800">
            <div className="max-w-2xl mx-auto text-center">
              <span className="text-xs font-semibold text-primary uppercase tracking-widest">Keşfedin</span>
              <h2 className="text-3xl font-semibold text-white mt-3 mb-4">Hızlı Erişim</h2>
              <p className="text-sm text-default-300 font-medium leading-relaxed">
                İşinizi kolaylaştıracak özelliklerimizi keşfedin ve çeşitli sistemlerle entegre çalışma yeteneğine sahip olduğumuz entegrasyonlarımızı gözden geçirin.
              </p>
            </div>
          </div>

          <div className="h-10 md:h-14"></div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-default-950/40 backdrop-blur-3xl rounded-2xl border border-white/5 transition-all duration-500 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5" data-aos="fade-right" data-aos-easing="ease" data-aos-duration="1000">
              <div className="sm:p-10 p-8">
                <div className="inline-flex items-center justify-center h-16 w-16 bg-primary/10 rounded-xl mb-10">
                  <svg viewBox="0 0 48 48" className="w-10 h-10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <style>{`
                      @keyframes he1-scan{0%{transform:translateY(0)}50%{transform:translateY(20px)}100%{transform:translateY(0)}}
                      @keyframes he1-pop{0%,100%{opacity:.4}50%{opacity:1}}
                      .he1-scan{animation:he1-scan 2.5s ease-in-out infinite}
                      .he1-pop{animation:he1-pop 2s ease-in-out infinite}
                    `}</style>
                    <rect x="6" y="4" width="36" height="40" rx="6" fill="#7c3aed" opacity="0.1" stroke="#a855f7" strokeWidth="1.5"/>
                    <rect x="12" y="12" width="18" height="3" rx="1.5" fill="#a855f7" opacity="0.6"/>
                    <rect x="12" y="19" width="24" height="2" rx="1" fill="#64748b" opacity="0.4"/>
                    <rect x="12" y="25" width="20" height="2" rx="1" fill="#64748b" opacity="0.4"/>
                    <rect x="12" y="31" width="22" height="2" rx="1" fill="#64748b" opacity="0.4"/>
                    <rect x="12" y="37" width="16" height="2" rx="1" fill="#64748b" opacity="0.4"/>
                    <rect x="4" y="10" width="40" height="2" rx="1" fill="#a855f7" opacity="0.3" className="he1-scan"/>
                    <circle cx="36" cy="13" r="3" fill="#10b981" className="he1-pop" opacity="0.7"/>
                    <path d="M34.5 13 L35.5 14 L37.5 12" stroke="white" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h2 className="text-2xl text-white font-medium mb-4">Özelliklerimiz</h2>
                <p className="text-base text-default-200 mb-6">
                  İşinizi kolaylaştıracak çeşitli özelliklerimizi keşfedin. Verimliliği artırmanıza yardımcı olacak çözümlerimiz hakkında daha fazla bilgi edinin.
                </p>
                <Link to="/ozellikler" className="inline-flex items-center justify-center gap-2 border border-white/10 text-white py-2 px-6 rounded-full hover:bg-primary-hover transition-all duration-300">
                  İncele <i data-lucide="move-right" className="h-5 w-5"></i>
                </Link>
              </div>
            </div>

            <div className="bg-default-950/40 backdrop-blur-3xl rounded-2xl border border-white/5 transition-all duration-500 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5" data-aos="fade-left" data-aos-easing="ease" data-aos-duration="1000">
              <div className="sm:p-10 p-8">
                <div className="inline-flex items-center justify-center h-16 w-16 bg-primary/10 rounded-xl mb-10">
                  <svg viewBox="0 0 48 48" className="w-10 h-10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <style>{`
                      @keyframes he2-zap{0%,100%{opacity:.4;transform:scale(1)}50%{opacity:1;transform:scale(1.15)}}
                      @keyframes he2-ring{0%{r:14;opacity:.3}50%{r:17;opacity:.1}100%{r:14;opacity:.3}}
                      .he2-zap{animation:he2-zap 1.5s ease-in-out infinite}
                      .he2-ring{animation:he2-ring 2s ease-in-out infinite}
                    `}</style>
                    <circle cx="24" cy="24" r="14" fill="#7c3aed" opacity="0.1" stroke="#a855f7" strokeWidth="1.2"/>
                    <circle cx="24" cy="24" className="he2-ring" fill="none" stroke="#a855f7" strokeWidth="0.8" opacity="0.2"/>
                    <g className="he2-zap" style={{transformOrigin: '24px 24px'}}>
                      <path d="M20 16 L14 26 L22 26 L18 36 L34 22 L24 22 L28 16 Z" fill="#a855f7" opacity="0.8"/>
                    </g>
                    <circle cx="10" cy="12" r="4" fill="#f8fafc" stroke="#3b82f6" strokeWidth="1"/>
                    <circle cx="10" cy="12" r="1.5" fill="#3b82f6" opacity="0.6"/>
                    <circle cx="38" cy="12" r="4" fill="#f8fafc" stroke="#10b981" strokeWidth="1"/>
                    <circle cx="38" cy="12" r="1.5" fill="#10b981" opacity="0.6"/>
                    <circle cx="10" cy="38" r="4" fill="#f8fafc" stroke="#f59e0b" strokeWidth="1"/>
                    <circle cx="10" cy="38" r="1.5" fill="#f59e0b" opacity="0.6"/>
                    <circle cx="38" cy="38" r="4" fill="#f8fafc" stroke="#ef4444" strokeWidth="1"/>
                    <circle cx="38" cy="38" r="1.5" fill="#ef4444" opacity="0.6"/>
                    <line x1="14" y1="14" x2="19" y2="19" stroke="#3b82f6" strokeWidth="0.8" opacity="0.4"/>
                    <line x1="34" y1="14" x2="29" y2="19" stroke="#10b981" strokeWidth="0.8" opacity="0.4"/>
                    <line x1="14" y1="36" x2="19" y2="30" stroke="#f59e0b" strokeWidth="0.8" opacity="0.4"/>
                    <line x1="34" y1="36" x2="29" y2="30" stroke="#ef4444" strokeWidth="0.8" opacity="0.4"/>
                  </svg>
                </div>
                <h2 className="text-2xl text-white font-medium mb-4">Entegrasyonlarımız</h2>
                <p className="text-base text-default-200 mb-6">
                  ISP Radius'un çeşitli sistemlerle entegre çalışma yeteneğine göz atın. Farklı entegrasyon seçeneklerimizi keşfedin ve iş akışınızı optimize edin.
                </p>
                <Link to="/entegrasyonlar" className="inline-flex items-center justify-center gap-2 border border-white/10 text-white py-2 px-6 rounded-full hover:bg-primary-hover transition-all duration-300">
                  İncele <i data-lucide="move-right" className="h-5 w-5"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 relative">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
        <div className="container">
          <div className="flex items-end justify-between mb-16" data-aos="fade-up" data-aos-easing="ease" data-aos-duration="800">
            <div className="max-w-2xl mx-auto text-center">
              <span className="text-xs font-semibold text-primary uppercase tracking-widest">Yardım Merkezi</span>
              <h2 className="text-3xl font-semibold text-white mt-3 mb-4">Sıkça Sorulan Sorular</h2>
              <p className="text-sm text-default-300 font-medium leading-relaxed">
                En yaygın sorularınıza cevaplar burada!<br/>
                ISP Radius ile ilgili sıkça sorulan soruları keşfedin ve ihtiyacınız olan bilgilere hızlıca erişin.
              </p>
            </div>
          </div>

          <div className="h-10 md:h-14"></div>

          {faqLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-default-400 mt-4">SSS verileri yükleniyor...</p>
            </div>
          ) : faqQuestions.length > 0 ? (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="hs-accordion-group space-y-4" data-aos="fade-right" data-aos-easing="ease" data-aos-duration="1000">
                {faqQuestions.slice(0, Math.ceil(faqQuestions.length / 2)).map((faq, index) => (
                  <div key={faq.id} className="hs-accordion border border-white/5 bg-default-950/40 backdrop-blur-3xl rounded-xl overflow-hidden transition-all duration-300 hover:border-primary/20" id={`faq-${faq.id}`}>
                    <button className="hs-accordion-toggle px-6 py-5 inline-flex items-center justify-between gap-x-3 w-full text-left text-white transition-all" aria-controls={`faq-${faq.id}`}>
                      <h5 className="text-base font-semibold flex items-center">
                        <span className="h-8 w-8 inline-flex items-center justify-center rounded-lg bg-primary/10 me-3 flex-shrink-0">
                          <i data-lucide="help-circle" className="h-4 w-4 stroke-primary"></i>
                        </span>
                        {faq.question}
                      </h5>
                      <i data-lucide="chevron-up" className="h-4 w-4 transition-all duration-500 hs-accordion-active:-rotate-180 flex-shrink-0 text-default-400"></i>
                    </button>
                    <div id={`faq-${faq.id}`} className="hs-accordion-content hidden w-full overflow-hidden transition-[height] duration-300" aria-labelledby={`faq-${faq.id}`}>
                      <div className="px-6 pb-5 pt-0 ps-[4.25rem]">
                        <p className="text-default-300 text-sm font-medium leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            <div className="hs-accordion-group space-y-4" data-aos="fade-left" data-aos-easing="ease" data-aos-duration="1000">
                {faqQuestions.slice(Math.ceil(faqQuestions.length / 2)).map((faq, index) => (
                  <div key={faq.id} className="hs-accordion border border-white/5 bg-default-950/40 backdrop-blur-3xl rounded-xl overflow-hidden transition-all duration-300 hover:border-primary/20" id={`faq-${faq.id}`}>
                    <button className="hs-accordion-toggle px-6 py-5 inline-flex items-center justify-between gap-x-3 w-full text-left text-white transition-all" aria-controls={`faq-${faq.id}`}>
                      <h5 className="text-base font-semibold flex items-center">
                        <span className="h-8 w-8 inline-flex items-center justify-center rounded-lg bg-primary/10 me-3 flex-shrink-0">
                          <i data-lucide="help-circle" className="h-4 w-4 stroke-primary"></i>
                        </span>
                        {faq.question}
                      </h5>
                      <i data-lucide="chevron-up" className="h-4 w-4 transition-all duration-500 hs-accordion-active:-rotate-180 flex-shrink-0 text-default-400"></i>
                    </button>
                    <div id={`faq-${faq.id}`} className="hs-accordion-content hidden w-full overflow-hidden transition-[height] duration-300" aria-labelledby={`faq-${faq.id}`}>
                      <div className="px-6 pb-5 pt-0 ps-[4.25rem]">
                        <p className="text-default-300 text-sm font-medium leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-default-400">Henüz SSS verisi bulunmuyor.</p>
          </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Home;